import { z } from 'zod';
import type {
  OnsiteLiveBranchSet,
  OnsiteLiveBranchSetWrite,
  OnsiteLiveSecurityGroup,
  OnsiteLiveSecurityGroupWrite,
  OnsiteLiveSubscriptionGroup,
  OnsiteLiveSubscriptionGroupWrite,
  OnsiteLiveUser,
  OnsiteLiveUserWrite,
  Subscription,
  SubscriptionWrite,
} from '@/types/oscar';

/**
 * Forms for the OnsiteLive user admin screens. Widths are the LEGACY column
 * widths (migration 141 = OnsiteLiveConfig): the SQ04 views hand these values
 * to OnBudget, the portal and OnFlow unchanged, so a value that would not fit
 * is refused, never cut. The server (OnsiteLiveAdminRules.cs) re-checks all of it.
 */

const optionalText = (max: number) => z.string().trim().max(max, `At most ${max} characters`);
const optionalEmail = (max: number) =>
  optionalText(max).refine((v) => v === '' || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v), 'Not a valid email address');
/** An id from a <select> or typed in: '' = none. Anything else must be a whole number. */
const optionalId = z.string().trim().regex(/^\d*$/, 'A UserId is a whole number');

// ── User ──────────────────────────────────────────────────────────────────────

export const userSchema = z
  .object({
    Username: z.string().trim().min(1, 'Username is required').max(50, 'At most 50 characters'),
    FullName: optionalText(50),
    Active: z.boolean(),
    Title: optionalText(100),
    EmailAddress: optionalEmail(100),
    Mobile: optionalText(50),
    Phone: optionalText(50),
    Branch: optionalText(20),
    OfficeLocation: optionalText(100),
    EmailDivert: optionalEmail(100),
    /** yyyy-mm-dd from <input type="date">, '' for none. */
    ExpiryDate: z.string(),
    TeamId: optionalId,
    SubscriptionBranch: optionalId,
    BusinessUnitBranchId: optionalId,
    ManagerId: optionalId,
    ExtManagerId: optionalId,
    ReplacementManagerUserId: optionalId,
    /** Who this account is in Oscar: a person OR a system account (CK_OnsiteLiveUser_OneOwner). */
    PersonId: optionalId,
    SystemAccountId: optionalId,
  })
  .superRefine((d, ctx) => {
    if (d.PersonId && d.SystemAccountId)
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['SystemAccountId'], message: 'A person OR a system account, not both' });
  });
export type UserFormData = z.infer<typeof userSchema>;

const idText = (v: number | null) => (v === null ? '' : String(v));
const textOrNull = (v: string) => (v.trim() === '' ? null : v.trim());
const idOrNull = (v: string) => (v === '' ? null : Number(v));

export const emptyUser: UserFormData = {
  Username: '', FullName: '', Active: true, Title: '', EmailAddress: '', Mobile: '', Phone: '', Branch: '',
  OfficeLocation: '', EmailDivert: '', ExpiryDate: '', TeamId: '', SubscriptionBranch: '', BusinessUnitBranchId: '',
  ManagerId: '', ExtManagerId: '', ReplacementManagerUserId: '', PersonId: '', SystemAccountId: '',
};

export function userToForm(u: OnsiteLiveUser): UserFormData {
  return {
    Username: u.Username,
    FullName: u.FullName ?? '',
    Active: u.Active,
    Title: u.Title ?? '',
    EmailAddress: u.EmailAddress ?? '',
    Mobile: u.Mobile ?? '',
    Phone: u.Phone ?? '',
    Branch: u.Branch ?? '',
    OfficeLocation: u.OfficeLocation ?? '',
    EmailDivert: u.EmailDivert ?? '',
    ExpiryDate: u.ExpiryDateTime ? u.ExpiryDateTime.slice(0, 10) : '',
    TeamId: idText(u.TeamId),
    SubscriptionBranch: idText(u.SubscriptionBranch),
    BusinessUnitBranchId: idText(u.BusinessUnitBranchId),
    ManagerId: idText(u.ManagerId),
    ExtManagerId: idText(u.ExtManagerId),
    ReplacementManagerUserId: idText(u.ReplacementManagerUserId),
    PersonId: idText(u.PersonId),
    SystemAccountId: idText(u.SystemAccountId),
  };
}

/**
 * Form → API. ⚠ An expiry date is sent as midnight, LOCAL to the legacy
 * column (DATETIME, no zone) — the offboarding job compares it in Sydney time.
 * '' becomes null, never 0 (Number('') is 0).
 */
export function toUserPayload(d: UserFormData): OnsiteLiveUserWrite {
  return {
    Username: d.Username.trim(),
    FullName: textOrNull(d.FullName),
    Active: d.Active,
    Title: textOrNull(d.Title),
    EmailAddress: textOrNull(d.EmailAddress),
    Mobile: textOrNull(d.Mobile),
    Phone: textOrNull(d.Phone),
    Branch: textOrNull(d.Branch),
    SubscriptionBranch: idOrNull(d.SubscriptionBranch),
    BusinessUnitBranchId: idOrNull(d.BusinessUnitBranchId),
    TeamId: idOrNull(d.TeamId),
    ManagerId: idOrNull(d.ManagerId),
    ExtManagerId: idOrNull(d.ExtManagerId),
    ReplacementManagerUserId: idOrNull(d.ReplacementManagerUserId),
    OfficeLocation: textOrNull(d.OfficeLocation),
    ExpiryDateTime: d.ExpiryDate ? `${d.ExpiryDate}T00:00:00` : null,
    EmailDivert: textOrNull(d.EmailDivert),
    PersonId: idOrNull(d.PersonId),
    SystemAccountId: idOrNull(d.SystemAccountId),
  };
}

// ── Leaving (person › Leaving tab) ────────────────────────────────────────────

/** The two OnsiteLive settings that only matter when a person leaves (Anthony, 25/10/2026). */
export const leavingSchema = z.object({
  EmailDivert: optionalEmail(100),
  ReplacementManagerUserId: optionalId,
});
export type LeavingFormData = z.infer<typeof leavingSchema>;

export const leavingToForm = (u: OnsiteLiveUser): LeavingFormData => ({
  EmailDivert: u.EmailDivert ?? '',
  ReplacementManagerUserId: idText(u.ReplacementManagerUserId),
});

/**
 * The user's whole current form with only these two changed: the update endpoint
 * applies every field, so everything else must go back exactly as it is.
 */
export function toLeavingPayload(u: OnsiteLiveUser, d: LeavingFormData): OnsiteLiveUserWrite {
  return {
    ...toUserPayload(userToForm(u)),
    EmailDivert: textOrNull(d.EmailDivert),
    ReplacementManagerUserId: idOrNull(d.ReplacementManagerUserId),
  };
}

// ── Security groups / subscription groups ─────────────────────────────────────

export const securityGroupSchema = z.object({
  Name: z.string().trim().min(1, 'Name is required').max(50, 'At most 50 characters'),
  Description: optionalText(100),
  Active: z.boolean(),
});
export type SecurityGroupFormData = z.infer<typeof securityGroupSchema>;

export const securityGroupToForm = (g: OnsiteLiveSecurityGroup | null): SecurityGroupFormData =>
  ({ Name: g?.Name ?? '', Description: g?.Description ?? '', Active: g?.Active ?? true });

export const toSecurityGroupPayload = (d: SecurityGroupFormData): OnsiteLiveSecurityGroupWrite =>
  ({ Name: d.Name.trim(), Description: textOrNull(d.Description), Active: d.Active });

export const subscriptionGroupSchema = z.object({
  Name: z.string().trim().min(1, 'Name is required').max(50, 'At most 50 characters'),
  Comments: optionalText(50),
  Inactive: z.boolean(),
});
export type SubscriptionGroupFormData = z.infer<typeof subscriptionGroupSchema>;

export const subscriptionGroupToForm = (g: OnsiteLiveSubscriptionGroup | null): SubscriptionGroupFormData =>
  ({ Name: g?.Name ?? '', Comments: g?.Comments ?? '', Inactive: g?.Inactive ?? false });

export const toSubscriptionGroupPayload = (d: SubscriptionGroupFormData): OnsiteLiveSubscriptionGroupWrite =>
  ({ Name: d.Name.trim(), Comments: textOrNull(d.Comments), Inactive: d.Inactive });

// ── Branch sets (SubscriptionBranches) ────────────────────────────────────────

/**
 * "1100, 1102;1102 1200" → ['1100', '1102', '1200']: split on comma, semicolon
 * or whitespace, drop blanks and repeats (case-insensitive), keep order — the
 * same rule as OnsiteLiveAdminRules.NormaliseBranchCodes.
 */
export function splitBranchCodes(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(/[,;\s]+/)) {
    const code = part.trim();
    if (!code || seen.has(code.toLowerCase())) continue;
    seen.add(code.toLowerCase());
    out.push(code);
  }
  return out;
}

export const branchSetSchema = z
  .object({
    Description: z.string().trim().min(1, 'Description is required').max(50, 'At most 50 characters'),
    BranchCodes: z.string(),
    Area: z.boolean(),
    Region: z.boolean(),
    Company: z.boolean(),
  })
  .superRefine((d, ctx) => {
    const codes = splitBranchCodes(d.BranchCodes);
    if (codes.length === 0)
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['BranchCodes'], message: 'At least one branch code' });
    const bad = codes.find((c) => c.length > 10 || !/^[A-Za-z0-9]+$/.test(c));
    if (bad)
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['BranchCodes'], message: `'${bad}' is not a branch code` });
  });
export type BranchSetFormData = z.infer<typeof branchSetSchema>;

export const branchSetToForm = (b: OnsiteLiveBranchSet | null): BranchSetFormData => ({
  Description: b?.Description ?? '',
  BranchCodes: splitBranchCodes(b?.BranchCodes ?? '').join(', '),
  Area: b?.Area ?? false,
  Region: b?.Region ?? false,
  Company: b?.Company ?? false,
});

export const toBranchSetPayload = (d: BranchSetFormData): OnsiteLiveBranchSetWrite => ({
  Description: d.Description.trim(),
  BranchCodes: splitBranchCodes(d.BranchCodes).join(','),
  Area: d.Area,
  Region: d.Region,
  Company: d.Company,
});

// ── Memberships ───────────────────────────────────────────────────────────────

/** Toggle one id in a membership list, keeping it sorted (the API answers sorted). */
export function toggleId(ids: number[], id: number): number[] {
  return ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id].sort((a, b) => a - b);
}

/** True when two id lists hold the same ids, whatever the order. */
export function sameIds(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  const s = new Set(a);
  return b.every((x) => s.has(x));
}

// ── Subscriptions (Migration Plan section 8) ──────────────────────────────────

/** The addresses in a To / CC / BCC field: split on ';' or ',', blanks ignored (server: OnsiteLiveSubscriptionRules.Addresses). */
export const addresses = (field: string): string[] =>
  field.split(/[;,]/).map((a) => a.trim()).filter((a) => a.length > 0);

const plainEmail = /^[^@\s<>"]+@[^@\s<>"]+\.[^@\s<>"]+$/;

/** Decision S1 (25/10/2026): free text, but every address must be a plain email. */
const recipients = z
  .string()
  .max(2000, 'At most 2000 characters')
  .superRefine((v, ctx) => {
    const bad = addresses(v).find((a) => !plainEmail.test(a));
    if (bad) ctx.addIssue({ code: z.ZodIssueCode.custom, message: `"${bad}" is not a valid email address` });
  });

const required = (label: string, max: number) =>
  z.string().trim().min(1, `${label} is required`).max(max, `At most ${max} characters`);

export const subscriptionSchema = z.object({
  Active: z.boolean(),
  Description: required('Description', 100),
  ReportName: required('Report name', 100),
  To: recipients,
  CC: recipients,
  BCC: recipients,
  RenderFormatId: z.string().regex(/^\d+$/, 'Choose a render format'),
  Subject: required('Subject', 1000),
  Comment: required('Comment', 2000),
  Param1: optionalText(4000),
  Param2: optionalText(4000),
  Param3: optionalText(4000),
  Param4: optionalText(4000),
  SubscriptionGrp: optionalId,
  DevelopmentNotes: z.string(),
});
export type SubscriptionFormData = z.infer<typeof subscriptionSchema>;

export const emptySubscription: SubscriptionFormData = {
  Active: true, Description: '', ReportName: '', To: '', CC: '', BCC: '', RenderFormatId: '',
  Subject: '', Comment: '', Param1: '', Param2: '', Param3: '', Param4: '', SubscriptionGrp: '', DevelopmentNotes: '',
};

export function subscriptionToForm(s: Subscription): SubscriptionFormData {
  return {
    Active: s.Active,
    Description: s.Description,
    ReportName: s.ReportName,
    To: s.To ?? '',
    CC: s.CC ?? '',
    BCC: s.BCC ?? '',
    RenderFormatId: idText(s.RenderFormatId),
    Subject: s.Subject ?? '',
    Comment: s.Comment ?? '',
    Param1: s.Param1 ?? '',
    Param2: s.Param2 ?? '',
    Param3: s.Param3 ?? '',
    Param4: s.Param4 ?? '',
    SubscriptionGrp: idText(s.SubscriptionGrp),
    DevelopmentNotes: s.DevelopmentNotes ?? '',
  };
}

/** Form → API. '' becomes null, never 0 (Number('') is 0). */
export function toSubscriptionPayload(d: SubscriptionFormData): SubscriptionWrite {
  return {
    Active: d.Active,
    Description: d.Description.trim(),
    ReportName: d.ReportName.trim(),
    To: textOrNull(d.To),
    CC: textOrNull(d.CC),
    BCC: textOrNull(d.BCC),
    RenderFormatId: idOrNull(d.RenderFormatId),
    Subject: d.Subject.trim(),
    Comment: d.Comment.trim(),
    Param1: textOrNull(d.Param1),
    Param2: textOrNull(d.Param2),
    Param3: textOrNull(d.Param3),
    Param4: textOrNull(d.Param4),
    SubscriptionGrp: idOrNull(d.SubscriptionGrp),
    DevelopmentNotes: textOrNull(d.DevelopmentNotes),
  };
}

// Parameter 3, exactly as OnBudget's SelectBranch writes it (server: OnsiteLiveSubscriptionRules.BranchParam).
export type BranchScope = 'branches' | 'areas' | 'regions';
const AREA_PREFIX = 'Branches In Area ';
const REGION_PREFIX = 'Branches In Region ';

/** "1200,1201" | "Branches In Area 3,5" | "Branches In Region 1" → kind + ids. */
export function parseBranchParam(value: string): { scope: BranchScope; ids: string[] } {
  const v = value.trim();
  const upper = v.toUpperCase();
  if (upper.startsWith(AREA_PREFIX.toUpperCase())) return { scope: 'areas', ids: splitIds(v.slice(AREA_PREFIX.length)) };
  if (upper.startsWith(REGION_PREFIX.toUpperCase())) return { scope: 'regions', ids: splitIds(v.slice(REGION_PREFIX.length)) };
  return { scope: 'branches', ids: splitIds(v) };
}

export function formatBranchParam(scope: BranchScope, ids: string[]): string {
  const list = ids.map((i) => i.trim()).filter((i) => i.length > 0).join(',');
  if (!list) return '';
  return scope === 'areas' ? AREA_PREFIX + list : scope === 'regions' ? REGION_PREFIX + list : list;
}

/** A comma list of codes (sites, account managers, branch ids) as OnBudget stores them. */
export const splitIds = (value: string): string[] => value.split(',').map((i) => i.trim()).filter((i) => i.length > 0);

